import { SxProps } from '@mui/material';
import AsanVerifyingView from './AsanVerifyingView';
import useAsanSigning from '../model/useAsanSigning';
import ErrorModal from 'app/components/ErrorModal';
import ResultDialog from 'app/shared/ui/elements/ResultDialog';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

interface Props {
  verifyCode?: string;
  asanModalType?: string;
  btnSx?: SxProps;
  close?: Dispatch<SetStateAction<boolean>>;
  timerTime?: any;
  pathUrl?: any;
  businessKey?: any;
  isRegistration?: boolean;
  title?: string;
  redirectUrl?: string;
  isClosedAsanModal?: boolean;
  handleRedirect?: VoidFunction;
  repeat_action?: string;
  task_reference?: string;
  onResponseHandled?: (data?: any) => void;
  /** Success dialog button handler (e.g. form-CSI). When missing, the buttons fall back to plain navigation. */
  onSuccessAction?: ((btnType: string, response?: any) => void) | null;
}
const AsanImza = (props: Props) => {
  const {
    verifyCode,
    asanModalType,
    btnSx,
    close,
    timerTime,
    pathUrl,
    businessKey,
    isRegistration = false,
    title,
    redirectUrl,
    isClosedAsanModal,
    handleRedirect,
    repeat_action,
    task_reference,
    onResponseHandled,
    onSuccessAction,
  } = props;
  const { t } = useTranslation();
  const { state, resetState, dataAsanImza, locationState, handleCancel, handleRepeatCode } =
    useAsanSigning({
      pathUrl,
      businessKey,
      asanModalType,
      isRegistration,
      onResponse: handleRedirect,
      onResponseHandled,
      repeatAction: repeat_action,
      taskReference: task_reference,
    });

  const location = useLocation();
  const lang = location.pathname.substring(0, 3);

  // Routing below mirrors the legacy SuccessModal `sign` / `unsuccess` branches 1:1.
  const handleSuccessGoHome = () => {
    if (onSuccessAction) {
      onSuccessAction('go-home', dataAsanImza);

      return;
    }
    window.location.href = `${lang}/home-page`;
    resetState();
    close?.(false);
  };

  const handleSuccessClose = () => {
    const successRedirectUrl = redirectUrl ?? dataAsanImza?.redirect_url;
    if (successRedirectUrl) {
      window.location.href = `${lang}/${successRedirectUrl}`;

      return;
    }
    if (onSuccessAction) {
      onSuccessAction('close', dataAsanImza);

      return;
    }
    window.location.href = `${lang}/transfer-page`;
    resetState();
  };

  const handleErrorClose = () => {
    if (isClosedAsanModal) {
      close?.(false);

      return;
    }
    if (redirectUrl) {
      window.location.href = `${lang}/${redirectUrl}`;

      return;
    }
    // TODO(IB-1310 follow-up): defaults to transfer-page for every product (legacy behavior).
    window.location.href = `${lang}/transfer-page`;
    resetState();
  };

  if (state.status === 'success') {
    return (
      <ResultDialog
        open
        variant='success'
        title={title ?? t('commonTerms.paymentSigned')}
        actions={[
          {
            label: t('commonActions.goToHomePage'),
            onClick: handleSuccessGoHome,
            color: 'primary',
          },
          { label: t('commonActions.close'), onClick: handleSuccessClose, color: 'secondary' },
        ]}
      />
    );
  }

  if (state.status === 'error') {
    return <ErrorModal open title={t('commonTerms.paymentNotSigned')} onClose={handleErrorClose} />;
  }

  return (
    <AsanVerifyingView
      asanModalType={asanModalType}
      btnSx={btnSx}
      verificationCode={
        locationState?.verification_code !== undefined
          ? locationState?.verification_code
          : (dataAsanImza?.verification_code ?? verifyCode)
      }
      timerTime={locationState?.timer !== undefined ? locationState?.timer : timerTime}
      isErrorResult={dataAsanImza?.result === 'error'}
      error={dataAsanImza?.error}
      onRepeat={handleRepeatCode}
      onCancel={handleCancel}
      onClose={() => close(false)}
    />
  );
};

export default AsanImza;
